import React, { useEffect, useState } from "react";
import {
  Clock, Calendar, Lock, ToggleLeft, AlertTriangle, CheckCircle, Play,
  Save, X, Mail, Settings as SettingsIcon, ShieldCheck, Loader2,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const WEEKDAYS = [
  { v: "monday", l: "Mon" }, { v: "tuesday", l: "Tue" }, { v: "wednesday", l: "Wed" },
  { v: "thursday", l: "Thu" }, { v: "friday", l: "Fri" }, { v: "saturday", l: "Sat" },
  { v: "sunday", l: "Sun" },
];
const AUDIENCE_OPTS = [
  { v: "client", l: "Client only" },
  { v: "internal", l: "Internal only" },
  { v: "both", l: "Both" },
];
const STATUS_BADGE = {
  draft_generated: { l: "Draft generated", cls: "bg-[#7A6B5F] text-white" },
  awaiting_approval: { l: "Awaiting approval", cls: "bg-[#D4A963] text-[#1C110C]" },
  sent: { l: "Sent", cls: "bg-[#3F6E2C] text-white" },
  failed: { l: "Failed", cls: "bg-[#A3141A] text-white" },
  skipped: { l: "Skipped", cls: "bg-[#1C110C] text-white" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Dubai",
    });
  } catch { return iso; }
}

/* ---------- Inline editor ---------- */
function SettingsEditor({ slug, initial, onClose, onSaved }) {
  const [s, setS] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recipientDraft, setRecipientDraft] = useState({ daily: "", weekly: "" });

  const update = (k, v) => setS((cur) => ({ ...cur, [k]: v }));
  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1";

  const addRecipient = (kind) => {
    const v = (recipientDraft[kind] || "").trim().toLowerCase();
    if (!v || !v.includes("@")) return;
    const field = `${kind}_recipients`;
    if ((s[field] || []).includes(v)) return;
    update(field, [...(s[field] || []), v]);
    setRecipientDraft((cur) => ({ ...cur, [kind]: "" }));
  };
  const removeRecipient = (kind, email) => {
    const field = `${kind}_recipients`;
    update(field, (s[field] || []).filter((x) => x !== email));
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const d = await portalApi.patchAutomation(slug, s);
      onSaved && onSaved(d.automation);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="portal-automation-editor" className="bg-white w-full max-w-[760px] border border-[#6E1F2C]/20 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F] inline-flex items-center gap-1.5"><Lock size={10} /> Admin only</p>
            <h3 className="font-serif-display text-2xl text-[#1C110C] mt-1">Report automation settings</h3>
            <p className="text-[11px] text-[#7A6B5F] mt-1">All times are Asia / Dubai. Reports are saved as drafts until approved unless you switch off &ldquo;requires approval&rdquo;.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Daily */}
          <section className="border border-[#6E1F2C]/15 p-4">
            <div className="flex items-center gap-3 mb-3">
              <input
                type="checkbox" data-testid="portal-automation-daily-enabled"
                checked={!!s.daily_enabled}
                onChange={(e) => update("daily_enabled", e.target.checked)}
                className="accent-[#6E1F2C] scale-125"
              />
              <Clock size={15} className="text-[#6E1F2C]" />
              <h4 className="font-serif-display text-lg text-[#1C110C]">Daily report</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Run at (UAE time)</label>
                <input
                  type="time" data-testid="portal-automation-daily-time"
                  value={s.daily_time || "18:00"}
                  onChange={(e) => update("daily_time", e.target.value)}
                  className={cls}
                />
              </div>
              <div>
                <label className={lbl}>Default recipients</label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {(s.daily_recipients || []).map((e) => (
                    <span key={e} data-testid={`portal-automation-daily-recipient-${e}`} className="text-[10px] bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-0.5 inline-flex items-center gap-1">
                      {e}
                      <button onClick={() => removeRecipient("daily", e)} className="text-[#A3141A]">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input
                    type="email" placeholder="name@example.com"
                    data-testid="portal-automation-daily-recipient-input"
                    value={recipientDraft.daily}
                    onChange={(e) => setRecipientDraft((c) => ({ ...c, daily: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient("daily"); } }}
                    className={cls}
                  />
                  <button type="button" onClick={() => addRecipient("daily")} className="bg-[#6E1F2C] text-white px-3 text-[10px] tracking-[0.2em] uppercase">Add</button>
                </div>
              </div>
            </div>
          </section>

          {/* Weekly */}
          <section className="border border-[#6E1F2C]/15 p-4">
            <div className="flex items-center gap-3 mb-3">
              <input
                type="checkbox" data-testid="portal-automation-weekly-enabled"
                checked={!!s.weekly_enabled}
                onChange={(e) => update("weekly_enabled", e.target.checked)}
                className="accent-[#6E1F2C] scale-125"
              />
              <Calendar size={15} className="text-[#6E1F2C]" />
              <h4 className="font-serif-display text-lg text-[#1C110C]">Weekly report</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={lbl}>Day</label>
                <div className="flex gap-1 flex-wrap">
                  {WEEKDAYS.map((d) => (
                    <button
                      key={d.v} type="button"
                      data-testid={`portal-automation-weekly-day-${d.v}`}
                      onClick={() => update("weekly_day", d.v)}
                      className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 border ${
                        s.weekly_day === d.v
                          ? "bg-[#6E1F2C] text-white border-[#6E1F2C]"
                          : "bg-white text-[#6E1F2C] border-[#6E1F2C]/25"
                      }`}
                    >
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={lbl}>Run at (UAE time)</label>
                <input
                  type="time" data-testid="portal-automation-weekly-time"
                  value={s.weekly_time || "18:00"}
                  onChange={(e) => update("weekly_time", e.target.value)}
                  className={cls}
                />
              </div>
              <div>
                <label className={lbl}>Default recipients</label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {(s.weekly_recipients || []).map((e) => (
                    <span key={e} data-testid={`portal-automation-weekly-recipient-${e}`} className="text-[10px] bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-0.5 inline-flex items-center gap-1">
                      {e}
                      <button onClick={() => removeRecipient("weekly", e)} className="text-[#A3141A]">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input
                    type="email" placeholder="name@example.com"
                    data-testid="portal-automation-weekly-recipient-input"
                    value={recipientDraft.weekly}
                    onChange={(e) => setRecipientDraft((c) => ({ ...c, weekly: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient("weekly"); } }}
                    className={cls}
                  />
                  <button type="button" onClick={() => addRecipient("weekly")} className="bg-[#6E1F2C] text-white px-3 text-[10px] tracking-[0.2em] uppercase">Add</button>
                </div>
              </div>
            </div>
          </section>

          {/* Behaviour */}
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">Behaviour</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <div>
                <label className={lbl}>Audience</label>
                <div className="flex gap-1">
                  {AUDIENCE_OPTS.map((o) => (
                    <button
                      key={o.v} type="button"
                      data-testid={`portal-automation-audience-${o.v}`}
                      onClick={() => update("audience", o.v)}
                      className={`text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border ${
                        s.audience === o.v
                          ? "bg-[#6E1F2C] text-white border-[#6E1F2C]"
                          : "bg-white text-[#6E1F2C] border-[#6E1F2C]/25"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-2 text-[11px] text-[#1C110C] cursor-pointer mt-5">
                <input
                  type="checkbox" data-testid="portal-automation-skip-empty"
                  checked={!!s.skip_empty_reports}
                  onChange={(e) => update("skip_empty_reports", e.target.checked)}
                  className="accent-[#6E1F2C] mt-0.5"
                />
                <span>Skip generation when no daily updates / comments exist for the period</span>
              </label>
              <label className="flex items-start gap-2 text-[11px] text-[#1C110C] cursor-pointer">
                <input
                  type="checkbox" data-testid="portal-automation-requires-admin"
                  checked={!!s.requires_admin_approval}
                  onChange={(e) => update("requires_admin_approval", e.target.checked)}
                  className="accent-[#6E1F2C] mt-0.5"
                />
                <span>Require Main Admin approval before send</span>
              </label>
              <label className="flex items-start gap-2 text-[11px] text-[#1C110C] cursor-pointer">
                <input
                  type="checkbox" data-testid="portal-automation-requires-pm"
                  checked={!!s.requires_pm_approval}
                  onChange={(e) => update("requires_pm_approval", e.target.checked)}
                  className="accent-[#6E1F2C] mt-0.5"
                />
                <span>Require Project Manager approval before send</span>
              </label>
              <label className={`flex items-start gap-2 text-[11px] text-[#1C110C] cursor-pointer ${
                (s.requires_admin_approval || s.requires_pm_approval) ? "opacity-50" : ""
              }`}>
                <input
                  type="checkbox" data-testid="portal-automation-auto-send"
                  checked={!!s.auto_send}
                  disabled={s.requires_admin_approval || s.requires_pm_approval}
                  onChange={(e) => update("auto_send", e.target.checked)}
                  className="accent-[#6E1F2C] mt-0.5"
                />
                <span>
                  Auto-send if no approval required
                  {(s.requires_admin_approval || s.requires_pm_approval) && (
                    <span className="block text-[10px] text-[#7A6B5F] italic">Disabled while approval is required.</span>
                  )}
                </span>
              </label>
            </div>
          </section>

          {/* Financial inclusion (admin-only feature) */}
          <section className="border border-[#D4A963]/30 bg-[#FAF5EC]/40 p-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F] inline-flex items-center gap-1.5 mb-3">
              <Lock size={10} /> Financial snapshot
            </p>
            <label className="flex items-start gap-2 text-[12px] text-[#1C110C] cursor-pointer mb-2">
              <input
                type="checkbox" data-testid="portal-automation-include-fin-internal"
                checked={!!s.include_financials_internal}
                onChange={(e) => update("include_financials_internal", e.target.checked)}
                className="accent-[#6E1F2C] mt-0.5"
              />
              <span>Include financial snapshot in <strong>internal</strong> reports</span>
            </label>
            <label className="flex items-start gap-2 text-[12px] text-[#1C110C] cursor-pointer">
              <input
                type="checkbox" data-testid="portal-automation-include-fin-client"
                checked={!!s.include_financials_client}
                onChange={(e) => update("include_financials_client", e.target.checked)}
                className="accent-[#6E1F2C] mt-0.5"
              />
              <span>Include client-shared financials in <strong>client</strong> reports <em className="text-[10px] text-[#7A6B5F]">(honours per-section &ldquo;share with client&rdquo; toggles)</em></span>
            </label>
          </section>

          {err && <p className="text-[11px] text-[#A3141A]">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 bg-[#FAF5EC]/40">
          <button type="button" onClick={onClose} className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] px-4 py-2">Cancel</button>
          <button
            type="button" onClick={submit} disabled={busy}
            data-testid="portal-automation-save"
            className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {busy ? "Saving" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Main card ---------- */
export function AutomationCard({ slug, isAdmin }) {
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [err, setErr] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    portalApi.getAutomation(slug)
      .then((d) => { if (alive) setSettings(d.automation); })
      .catch((e) => { if (alive) setErr(e.message); });
    portalApi.adminAutomationLog({ limit: 8 })
      .then((d) => { if (alive) setLog((d.items || []).filter((it) => it.project_slug === slug).slice(0, 8)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug, refreshKey]);

  const triggerNow = async (kind) => {
    try {
      await portalApi.runAutomationNow(slug, kind);
      setRefreshKey((k) => k + 1);
    } catch (e) { setErr(e.message); }
  };

  if (err) return null;
  if (!settings) {
    return (
      <div data-testid="portal-automation-card-loading" className="bg-white border border-[#6E1F2C]/12 h-32 animate-pulse mb-6" />
    );
  }

  const dailyOn = settings.daily_enabled;
  const weeklyOn = settings.weekly_enabled;
  const requiresApproval = settings.requires_admin_approval || settings.requires_pm_approval;

  return (
    <div
      data-testid="portal-automation-card"
      className="bg-white border border-[#6E1F2C]/15 mb-7"
    >
      <div className={`absolute top-0 left-0 right-0 h-[3px] ${dailyOn || weeklyOn ? "bg-[#3F6E2C]" : "bg-[#7A6B5F]"}`} />
      <div className="p-5 border-b border-[#6E1F2C]/10 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F] inline-flex items-center gap-1.5">
            <Lock size={10} /> Admin only · Asia / Dubai timezone
          </p>
          <h3 className="font-serif-display text-xl text-[#1C110C] mt-0.5 inline-flex items-center gap-2">
            <SettingsIcon size={16} className="text-[#6E1F2C]" />
            Report automation
          </h3>
          <p className="text-[11px] text-[#7A6B5F] mt-1">
            {dailyOn || weeklyOn
              ? `Scheduler active. ${dailyOn ? `Daily at ${settings.daily_time}` : ""}${dailyOn && weeklyOn ? " · " : ""}${weeklyOn ? `Weekly on ${settings.weekly_day} at ${settings.weekly_time}` : ""}.`
              : "Automation is OFF. Drafts will not be generated automatically until you enable it."}
            {requiresApproval && (dailyOn || weeklyOn) && (
              <span className="block text-[#A6822F] mt-1">
                <ShieldCheck size={10} className="inline mr-1" />Approval required — reports save as drafts until you review.
              </span>
            )}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => triggerNow("daily")}
              data-testid="portal-automation-run-daily"
              className="border border-[#6E1F2C] text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white px-3 py-2 text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1.5"
            >
              <Play size={11} /> Run daily now
            </button>
            <button
              type="button"
              onClick={() => triggerNow("weekly")}
              data-testid="portal-automation-run-weekly"
              className="border border-[#6E1F2C] text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white px-3 py-2 text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1.5"
            >
              <Play size={11} /> Run weekly now
            </button>
            <button
              type="button"
              onClick={() => setShowEditor(true)}
              data-testid="portal-automation-edit"
              className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-3 py-2 text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1.5"
            >
              <SettingsIcon size={11} /> Configure
            </button>
          </div>
        )}
      </div>

      {/* Recent automation events */}
      <div className="p-5">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Recent runs</p>
        {!log || log.length === 0 ? (
          <p className="text-[11px] text-[#7A6B5F] italic">No automation activity yet on this project.</p>
        ) : (
          <ul data-testid="portal-automation-log-list" className="space-y-1.5">
            {log.map((it, i) => {
              const b = STATUS_BADGE[it.status] || { l: it.status, cls: "bg-[#7A6B5F] text-white" };
              return (
                <li key={i} data-testid={`portal-automation-log-${it.kind}-${it.status}-${i}`} className="flex items-center gap-3 text-[11px]">
                  <span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 ${b.cls}`}>{b.l}</span>
                  <span className="text-[#1C110C] font-medium">{it.kind === "daily" ? "Daily" : "Weekly"}</span>
                  <span className="text-[#7A6B5F]">{fmtTime(it.created_at)}</span>
                  {it.meta?.trigger === "manual" && <span className="text-[#A6822F]">manual</span>}
                  {it.error && <span className="text-[#A3141A] truncate">· {it.error}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showEditor && (
        <SettingsEditor
          slug={slug}
          initial={settings}
          onClose={() => setShowEditor(false)}
          onSaved={(next) => { setSettings(next); setRefreshKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
